import React from "react";
import { Link } from "react-router-dom";

import { formatBytes } from "utils/format";
import Loading from "components/Loading";

import {
  useSchemaViewListQuery,
  SchemaViewListQuery,
} from "./gql/Query.generated";
import { useRoutes } from "utils/routes";
import LazyLoader from "components/LazyLoader";
import Grid from "components/Grid";
import { SortOrder } from "components/Grid/util";
import { useQueryStringSort } from "utils/hooks";

type SchemaView = SchemaViewListQuery["getSchemaViews"][number];

interface Props {
  searchTerm: string | null;
  databaseId: string;
}

const FETCH_BATCH_SIZE = 50;

const Table: React.FunctionComponent<Props> = ({ searchTerm, databaseId }) => {
  const [sortOpts, setSortOpts] = useQueryStringSort<keyof SchemaView>({
    param: "sort",
    sortBy: "viewName",
    sortDirection: "ASC",
  });

  const { data, loading, error, fetchMore } = useSchemaViewListQuery({
    variables: {
      offset: 0,
      limit: FETCH_BATCH_SIZE,
      filter: searchTerm,
      sortBy: sortOpts.sortBy,
      sortDirection: sortOpts.sortDirection,
      databaseId,
    },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const schemaViews = data.getSchemaViews;

  const fetchMoreData = (loadedCount: number) => {
    return new Promise<boolean>((resolve) => {
      const offset = loadedCount;
      fetchMore({
        variables: { offset, limit: FETCH_BATCH_SIZE },
        updateQuery: (prev, { fetchMoreResult }) => {
          const fetched = fetchMoreResult.getSchemaViews;
          resolve(fetched.length > 0);
          if (!prev || !prev.getSchemaViews) {
            return fetchMoreResult;
          }
          return {
            ...prev,
            getSchemaViews: [...prev.getSchemaViews, ...fetched],
          };
        },
      });
    });
  };

  const sortedOrder = sortOpts.sortDirection;
  const sortedBy = sortOpts.sortBy as keyof SchemaView;

  function handleSort(sortBy: keyof SchemaView, sortOrder: SortOrder) {
    setSortOpts({
      sortBy,
      sortDirection: sortOrder,
    });
  }

  return (
    <LazyLoader loadedCount={schemaViews.length} loadMore={fetchMoreData}>
      <Grid
        striped
        data={schemaViews}
        columns={[
          {
            field: "schemaName",
            header: "Schema",
            width: "120px",
          },
          {
            field: "viewName",
            header: "view Name",
            width: "200px",
            renderer: function ViewNameCell({ rowData }) {
              return (
                <NameAndIssuesCell
                  databaseId={databaseId}
                  schemaView={rowData}
                />
              );
            },
          },
          {
            field: "viewDefinition",
            header: "Definition",
            width: "1fr",
            style: "query",
            renderer: function ViewDefinitionCell({ fieldData }) {
              return fieldData.trim();
            },
          },
          {
            field: "materializedView",
            header: "Materialized",
            width: "100px",
            defaultSortOrder: "DESC",
            renderer: function IsMaterializedViewCell({ fieldData }) {
              return fieldData ? "Yes" : "No";
            },
          },
          {
            field: "sizeBytes",
            header: "Data Size",
            width: "120px",
            style: "number",
            defaultSortOrder: "DESC",
            nullValue: "n/a",
            renderer: function DataSizeCell({ fieldData }) {
              return formatBytes(fieldData);
            },
          },
        ]}
        sortedBy={sortedBy}
        sortedOrder={sortedOrder}
        handleSort={handleSort}
      />
    </LazyLoader>
  );
};

const NameAndIssuesCell: React.FunctionComponent<{
  databaseId: string;
  schemaView: SchemaView;
}> = ({ databaseId, schemaView }) => {
  const { databaseView } = useRoutes();
  return (
    <span>
      <Link to={databaseView(databaseId, schemaView.id)}>
        {schemaView.viewName}
      </Link>
      {schemaView.issues.map((i) => (
        <Link
          to={databaseView(databaseId, schemaView.id)}
          className={`state-${i.severity}`}
          title={`${i.references[0].name} ${i.description}`}
          key={i.id}
        >
          <i className="icon-exclamation-sign" />
        </Link>
      ))}
    </span>
  );
};

export default Table;
